// ══════════════════════════════════════════════════════════════
// COMPASS – User Effects
// ══════════════════════════════════════════════════════════════
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, from } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { UserActions } from './user.actions';
import { ToastActions } from '../toast/toast.actions';
import { NotificationActions } from '../notification/notification.actions';
import { ManagementCompanyActions } from '../management-company/management-company.actions';
import { selectSelectedCompanyId } from '../management-company/management-company.selectors';
import { AuthService } from '../../services/auth.service';
import { Logger } from '../../services/logger.service';

@Injectable()
export class UserEffects {
  private readonly actions$ = inject(Actions);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly logger = inject(Logger);

  /**
   * Initialize authentication on app startup.
   * Roles and permissions are no longer derived from the JWT — they will be
   * loaded from the permissions API after management companies load.
   */
  readonly initializeAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.initializeAuth),
      switchMap(() =>
        from(this.authService.init()).pipe(
          map((isAuthenticated) => {
            const user = isAuthenticated ? this.authService.getCurrentUser() : null;

            // Roles/permissions are empty here — they'll be set by the
            // permissions API via ManagementCompanyEffects.
            const safeUser = user ? { ...user, roles: [], permissions: [] } : null;

            return UserActions.initializeAuthSuccess({
              user: safeUser,
              isAuthenticated,
            });
          }),
          catchError((error) => {
            this.logger.error('Auth initialization failed:', error);
            return of(UserActions.initializeAuthFailure({ error: error.message }));
          })
        )
      )
    )
  );

  /**
   * After auth init success, trigger company loading if authenticated.
   * This kicks off the chain: loadCompanies → autoSelectCompany → loadPermissions → setUserPermissions.
   */
  readonly loadCompaniesAfterAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.initializeAuthSuccess),
      filter(({ isAuthenticated }) => isAuthenticated),
      map(() => ManagementCompanyActions.loadCompanies())
    )
  );

  /**
   * Start in-app notification polling when the user is authenticated.
   * Stops on logout or session expiry (see stopNotifications$ below).
   */
  readonly startNotificationsAfterAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.initializeAuthSuccess),
      filter(({ isAuthenticated }) => isAuthenticated),
      map(() => NotificationActions.startBackgroundRefresh())
    )
  );

  /**
   * Stop in-app notification polling on logout or session expiry.
   */
  readonly stopNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.logoutSuccess, UserActions.sessionExpired),
      map(() => NotificationActions.stopBackgroundRefresh())
    )
  );

  /**
   * Handle user login - triggers redirect to Keycloak
   */
  readonly login$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.login),
        tap(() => {
          this.authService.login();
        })
      ),
    { dispatch: false }
  );

  /**
   * Handle user logout
   */
  readonly logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.logout),
      switchMap(() =>
        this.authService.logout().pipe(
          map(() => UserActions.logoutSuccess()),
          catchError(() => of(
            UserActions.logoutSuccess(),
            ToastActions.showToast({
              message: 'NOTIFICATIONS.LOGOUT_SERVER_ERROR',
              notificationType: 'warning',
            })
          ))
        )
      )
    )
  );

  /**
   * Handle token refresh.
   * After refresh, re-dispatch loadPermissions for the current company.
   */
  readonly refreshToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.refreshToken),
      switchMap(() =>
        this.authService.refreshToken().pipe(
          withLatestFrom(this.store.select(selectSelectedCompanyId)),
          switchMap(([, companyId]) => {
            const rawUser = this.authService.getCurrentUser();
            const token = this.authService.getToken();
            if (!rawUser || !token) {
              throw new Error('Failed to get user after token refresh');
            }

            // Keep roles/permissions empty — they'll be refreshed from API
            const user = { ...rawUser, roles: [], permissions: [] };

            const tokenExpiresAt = new Date().getTime() + 5 * 60 * 1000;
            const refreshTokenExpiresAt = new Date().getTime() + 30 * 60 * 1000;

            const actions: ReturnType<typeof UserActions.refreshTokenSuccess | typeof ManagementCompanyActions.loadPermissions>[] = [
              UserActions.refreshTokenSuccess({ user, tokenExpiresAt, refreshTokenExpiresAt }),
            ];

            if (companyId) {
              actions.push(ManagementCompanyActions.loadPermissions({ companyId }));
            }

            return actions;
          }),
          catchError((error) =>
            of(UserActions.refreshTokenFailure({ error: error.message }))
          )
        )
      )
    )
  );

  /**
   * Handle token refresh failure by expiring the session
   */
  readonly refreshTokenFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.refreshTokenFailure),
      map(() => UserActions.sessionExpired())
    )
  );

  /**
   * Handle session expiration
   */
  readonly sessionExpired$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.sessionExpired),
      tap(() => {
        const attemptedUrl = this.router.url;
        if (attemptedUrl !== '/' && !attemptedUrl.startsWith('/auth')) {
          sessionStorage.setItem('compass_return_url', attemptedUrl);
        }

        this.authService.clearSession();
        this.router.navigate(['/auth/login'], {
          queryParams: { error: 'session-expired' },
        });
      }),
      map(() =>
        ToastActions.showToast({
          message: 'ERROR.SESSION_EXPIRED',
          notificationType: 'warning',
        })
      )
    )
  );

  /**
   * Handle forgot password request
   */
  readonly forgotPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.forgotPassword),
      switchMap(({ email }) =>
        this.authService.forgotPassword(email).pipe(
          switchMap(() => [
            UserActions.forgotPasswordSuccess(),
            ToastActions.showToast({
              message: 'AUTH.FORGOT_PASSWORD.SUCCESS',
              notificationType: 'success',
            }),
          ]),
          catchError((error) =>
            of(UserActions.forgotPasswordFailure({ error: error.message }))
          )
        )
      )
    )
  );

  /**
   * Show error notification on forgot password failure
   */
  readonly forgotPasswordFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.forgotPasswordFailure),
      map(({ error }) =>
        ToastActions.showToast({
          message: error,
          notificationType: 'error',
        })
      )
    )
  );

  /**
   * Handle reset password request
   */
  readonly resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.resetPassword),
      switchMap(({ token, password }) =>
        this.authService.resetPassword(token, password).pipe(
          switchMap(() => [
            UserActions.resetPasswordSuccess(),
            ToastActions.showToast({
              message: 'AUTH.RESET_PASSWORD.SUCCESS',
              notificationType: 'success',
            }),
          ]),
          tap(() => {
            this.router.navigate(['/auth/login']);
          }),
          catchError((error) =>
            of(UserActions.resetPasswordFailure({ error: error.message }))
          )
        )
      )
    )
  );

  /**
   * Show error notification on reset password failure
   */
  readonly resetPasswordFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.resetPasswordFailure),
      map(({ error }) =>
        ToastActions.showToast({
          message: error,
          notificationType: 'error',
        })
      )
    )
  );
}
